import React, { useEffect, useRef, useState } from 'react';
import { ModalProps } from '../../types/ui';
import { useResponsive } from '../../hooks';

/**
 * Standard modal component for consistent UI across the application
 * Updated to follow the mobile-friendly pattern of TransactionDetailModal
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  closeOnClickOutside = true
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Mobile swipe down to close gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientY);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > 100;
    if (isDownSwipe && isMobile && closeOnClickOutside) {
      onClose();
    }
    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!isOpen) return null;

  // Determine modal size class based on the size prop
  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return 'max-w-md';
      case 'medium':
        return 'max-w-lg';
      case 'large':
        return 'max-w-3xl';
      case 'full':
        return 'max-w-full mx-4';
      default:
        return 'max-w-lg';
    }
  };

  return (
    <div 
      id="modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn" 
      onClick={(e) => {
        const isBackdropClick = e.target === e.currentTarget;
        
        // Only close if explicitly enabled and it's a direct backdrop click
        if (isBackdropClick && closeOnClickOutside) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}>
      <div 
        id="modal-content"
        ref={modalRef} 
        className={`bg-white shadow-xl ${
          isMobile 
          ? 'fixed inset-x-0 bottom-0 rounded-b-none rounded-t-xl max-h-[90vh] overflow-y-auto animate-slideUp' 
          : `${getSizeClass()} w-full rounded-lg max-h-[90vh] overflow-y-auto animate-slideIn`
        }`}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Mobile swipe indicator */}
        {isMobile && (
          <div className="w-12 h-1.5 bg-primary-silver/50 rounded-full mx-auto mt-3 mb-1"></div>
        )}
        
        {/* Header */}
        {title && (
          <div className="flex justify-between items-center border-b border-primary-silver/30 px-6 py-4 sticky top-0 bg-white z-10">
            <h2 className="text-xl font-bold text-primary-charcoal">{title}</h2>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClose();
              }}
              className="text-primary-charcoal/70 hover:text-primary-charcoal focus:outline-none"
              aria-label="Kapat"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Content */}
        <div className="p-6" onClick={(e) => {
          // Click event handler remains but without the logging
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal; 